// Libs
import React, { useState, useContext } from "react";
import { useObserver } from "@shoothill/core";
import { useMediaQuery } from "@material-ui/core";
import styled from "styled-components";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";

// Views
import { ArmedForcesProofView } from "Views/Application/FormSections/EmploymentDetails/ArmedForcesProofView";

// Styles
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { AuditedAccountsView } from "./AuditedAccountsView";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledAddButton = styled(StyledButton)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0px;
    margin: 0 0 1px 0;
`;

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const EmploymentDetailsView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.employmentDetailsViewModel);
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;
    return useObserver(() => (
        <div onMouseDown={() => viewModel.restartTimer()} onKeyUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            {/* Regular in armed forces */}
            <Row>
                <CustomYesNoRadio
                    label="Are you a regular in the Armed Forces?"
                    fieldName={"regularInArmedForces"}
                    value={viewModel.model.regularInArmedForces}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("regularInArmedForces")}
                />
            </Row>
            {/* File upload for armed forces proof */}
            {viewModel.model.regularInArmedForces == true && (
                <ArmedForcesProofView applicationFormViewModel={props.applicationFormViewModel} employmentDetailsViewModel={viewModel} />
            )}
            {/* Employment status */}
            <Row>
                <CustomInput
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    type="select"
                    label="Which one best describes employment status?"
                    viewModel={viewModel}
                    fieldName="employmentStatus"
                    selectItems={storesContext.Domain.LookupStore.getEmploymentStatusOptions}
                    shrink={true}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                    onChange={() => {
                        viewModel.handleEmploymentStatusChange();
                        viewModel.handleInputChange();
                    }}
                />
            </Row>
            {/* Permanently Employed*/}
            {viewModel.model.employmentStatus == EmploymentStatusEnum.FullTime && (
                <>
                    {/* Job title and work full/part time */}
                    <Row>
                        <CustomInput
                            label="Job title / Occupation"
                            viewModel={viewModel}
                            fieldName="occupation"
                            placeHolder="Your job title / occupation"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    {/* Employers name telephone and address */}
                    <Row>
                        <CustomInput
                            label="Employer's name"
                            placeHolder="Your employer's name"
                            viewModel={viewModel}
                            fieldName="employerName"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                        <CustomInput
                            label="Employer's telephone"
                            placeHolder="Your employer's telephone"
                            viewModel={viewModel}
                            fieldName="employerTelephone"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Employer's address line 1"
                            placeHolder="Address line 1"
                            viewModel={viewModel}
                            fieldName="employerAddressLine1"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's address line 2"
                            placeHolder="Address line 2"
                            viewModel={viewModel}
                            fieldName="employerAddressLine2"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's address line 3"
                            placeHolder="Address line 3"
                            viewModel={viewModel}
                            fieldName="employerAddressLine3"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Employer's city/town"
                            placeHolder="City/Town"
                            viewModel={viewModel}
                            fieldName="employerCity"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's county"
                            placeHolder="County"
                            viewModel={viewModel}
                            fieldName="employerCounty"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's postcode"
                            placeHolder="Postcode"
                            viewModel={viewModel}
                            fieldName="employerPostcode"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomTwoSelects>
                            <CustomInput
                                id="first-select"
                                type="select"
                                viewModel={viewModel}
                                label="How long have you been employed by your company?"
                                fieldName="yearsAtCompany"
                                placeHolder={"Please select"}
                                selectItems={storesContext.Domain.LookupStore.getYearOptions}
                                shrink={true}
                                editMode={true}
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                                onChange={() => {
                                    viewModel.handleInputChange();
                                }}
                            />
                            <CustomInput
                                type="select"
                                viewModel={viewModel}
                                fieldName="monthsAtCompany"
                                placeHolder={"Please select"}
                                selectItems={storesContext.Domain.LookupStore.getMonthOptions}
                                shrink={true}
                                editMode={true}
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                                onChange={() => {
                                    viewModel.handleInputChange();
                                }}
                            />
                        </CustomTwoSelects>
                    </Row>
                    {/* Probationary period? */}
                    <Row>
                        <CustomYesNoRadio
                            label="Are you on a probationary period?"
                            fieldName={"onProbation"}
                            value={viewModel.model.onProbation}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("onProbation")}
                        />

                        {viewModel.model.onProbation == true && (
                            <CustomInput
                                type="select"
                                style={{ minWidth: "350px" }}
                                viewModel={viewModel}
                                label="Probation length"
                                fieldName="probationLength"
                                placeHolder={"Please select"}
                                selectItems={storesContext.Domain.LookupStore.getMonthOptions}
                                shrink={true}
                                editMode={true}
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                                onChange={() => {
                                    viewModel.handleInputChange();
                                }}
                            />
                        )}
                    </Row>
                </>
            )}
            {/* Fixed term contract */}
            {viewModel.model.employmentStatus == EmploymentStatusEnum.PartTime && (
                <>
                    {/* Job title and work full/part time */}
                    <Row>
                        <CustomInput
                            label="Job title / Occupation"
                            viewModel={viewModel}
                            fieldName="occupation"
                            placeHolder="Your job title / occupation"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    {/* Employers name telephone and address */}
                    <Row>
                        <CustomInput
                            label="Employer's name"
                            placeHolder="Your employer's name"
                            viewModel={viewModel}
                            fieldName="employerName"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                        <CustomInput
                            label="Employer's telephone"
                            placeHolder="Your employer's telephone"
                            viewModel={viewModel}
                            fieldName="employerTelephone"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Employer's address line 1"
                            placeHolder="Address line 1"
                            viewModel={viewModel}
                            fieldName="employerAddressLine1"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's address line 2"
                            placeHolder="Address line 2"
                            viewModel={viewModel}
                            fieldName="employerAddressLine2"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's address line 3"
                            placeHolder="Address line 3"
                            viewModel={viewModel}
                            fieldName="employerAddressLine3"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomInput
                            label="Employer's city/town"
                            placeHolder="City/Town"
                            viewModel={viewModel}
                            fieldName="employerCity"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's county"
                            placeHolder="County"
                            viewModel={viewModel}
                            fieldName="employerCounty"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <CustomInput
                            label="Employer's postcode"
                            placeHolder="Postcode"
                            viewModel={viewModel}
                            fieldName="employerPostcode"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    {/* Probationary period? */}
                    <Row>
                        <CustomYesNoRadio
                            label="Are you on a probationary period?"
                            fieldName={"onProbation"}
                            value={viewModel.model.onProbation}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("onProbation")}
                        />

                        {viewModel.model.onProbation == true && (
                            <CustomInput
                                type="select"
                                style={{ minWidth: "350px" }}
                                viewModel={viewModel}
                                label="Probation length"
                                fieldName="probationLength"
                                placeHolder={"Please select"}
                                selectItems={storesContext.Domain.LookupStore.getMonthOptions}
                                shrink={true}
                                editMode={true}
                                inputProps={{
                                    disabled: props.applicationFormViewModel.model.isSubmitted,
                                }}
                                onChange={() => {
                                    viewModel.handleInputChange();
                                }}
                            />
                        )}
                    </Row>
                </>
            )}
            {/* Self employed */}
            {viewModel.model.employmentStatus == EmploymentStatusEnum.SelfEmployed && (
                <>
                    {/* Employers name telephone and address */}
                    <Row>
                        <CustomInput
                            label="Company"
                            placeHolder="Your company's name"
                            viewModel={viewModel}
                            fieldName="employerName"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        {/* <CustomInput
                            type="select"
                            viewModel={viewModel}
                            label="Length of time self employed?"
                            fieldName="yearsSelfEmployed"
                            placeHolder={"Please select"}
                            selectItems={storesContext.Domain.LookupStore.getYearOptions()}
                            shrink={true}
                            editMode={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        /> */}
                        <CustomInput
                            type="select"
                            viewModel={viewModel}
                            fieldName="monthSelfEmployed"
                            placeHolder={"Please select"}
                            selectItems={storesContext.Domain.LookupStore.getMonthOptions}
                            shrink={true}
                            editMode={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        <CustomYesNoRadio
                            label="Can you provide at least 2 years' audited accounts?"
                            fieldName={"canProvideThreeYearsAuditAccounts"}
                            value={viewModel.model.canProvideThreeYearsAuditAccounts}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("canProvideThreeYearsAuditAccounts")}
                        />
                    </Row>
                    {/* File upload for audied accounts proof */}
                    {viewModel.model.canProvideThreeYearsAuditAccounts == true && (
                        <AuditedAccountsView applicationFormViewModel={props.applicationFormViewModel} employmentDetailsViewModel={viewModel} />
                    )}
                </>
            )}
        </div>
    ));
};
